/**
 * Kompresja zdjęć po stronie klienta przed uploadem do Supabase.
 * 
 * - Resize do max szerokości (domyślnie 1600px — wystarczająco na fullscreen)
 * - Konwersja do WebP (z fallback na JPEG dla starszych przeglądarek)
 * - Jakość 0.85 — praktycznie nieodróżnialna od oryginału
 * 
 * Typowy wynik: zdjęcie z telefonu 4000×3000 (3-5 MB) → 1600×1200 (~150-250 KB)
 */

const supportsWebP = (() => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
})();

/**
 * Kompresuje obraz do zadanej szerokości i jakości.
 * 
 * @param {File} file - Oryginalny plik (JPEG/PNG)
 * @param {object} options
 * @param {number} options.maxWidth - Max szerokość w px (domyślnie 1600)
 * @param {number} options.maxHeight - Max wysokość w px (domyślnie 1200)
 * @param {number} options.quality - Jakość 0-1 (domyślnie 0.85)
 * @returns {Promise<File>} - Skompresowany plik
 */
export const compressImage = (file, { 
  maxWidth = 1600, 
  maxHeight = 1200, 
  quality = 0.85 
} = {}) => {
  return new Promise((resolve) => {
    // Jeśli plik jest mały (<300KB), nie kompresuj — nie warto
    if (file.size < 300 * 1024) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Jeśli obraz już jest mniejszy niż max — zmniejsz tylko jakość
      if (width <= maxWidth && height <= maxHeight && file.size < 500 * 1024) {
        resolve(file);
        return;
      }

      // Oblicz nowe wymiary zachowując proporcje
      if (width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round(width * (maxHeight / height));
        height = maxHeight;
      }

      // Rysuj na canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      
      // Wyższa jakość skalowania
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Zawsze konwertuj do JPEG — Supabase Storage obsługuje JPEG
      // (WebP powodował błąd "mime type image/webp is not supported")
      const outputType = 'image/jpeg';
      const extension = 'jpg';

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // Fallback — zwróć oryginał
            resolve(file);
            return;
          }

          // Jeśli skompresowany jest WIĘKSZY niż oryginał (rzadkie), zwróć oryginał
          if (blob.size >= file.size) {
            resolve(file);
            return;
          }

          // Utwórz nowy File z oryginalną nazwą + nowe rozszerzenie
          const baseName = file.name.replace(/\.[^.]+$/, '');
          const compressedFile = new File(
            [blob], 
            `${baseName}.${extension}`, 
            { type: outputType }
          );

          resolve(compressedFile);
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // W razie błędu — zwróć oryginał
      resolve(file);
    };

    img.src = url;
  });
};

/**
 * Kompresuje zdjęcie profilowe (mniejszy max — avatar nie potrzebuje 1600px)
 */
export const compressProfilePhoto = (file) => 
  compressImage(file, { maxWidth: 800, maxHeight: 800, quality: 0.85 });
